import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../../../shared/supabase';
import { useAuth } from '../../../shared/config/AuthContext';
import { offlineStorage } from '../../../shared/utils/offlineStorage';
import { getCustomThemeSlug } from '../../../shared/api/themeService';
import { getPreviewThemeFromUrl, resolveThemePreview } from '../../../shared/utils/themePreview';
import { isCustomThemeActive } from '../../../../themes/registry';

const RestaurantContext = createContext();

// Default theme rendered to customers when a restaurant has no theme_id set.
export const DEFAULT_THEME_ID = 'a';
export const DEFAULT_THEME_TYPE = 'platform';
export const CUSTOM_THEME_ID = 'custom';

// Helper function to get URL parameters
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    restaurantId: urlParams.get('restaurant'),
    tableNumber: urlParams.get('table'),
  };
};

// Helper function to store in localStorage
const localStorageStore = {
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('localStorage.setItem failed:', error);
    }
  },
  get: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('localStorage.getItem failed:', error);
      return null;
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('localStorage.removeItem failed:', error);
    }
  },
  clear: () => {
    try {
      localStorage.removeItem('restaurantId');
      localStorage.removeItem('tableNumber');
      localStorage.removeItem('themeId');
      localStorage.removeItem('themeType');
      localStorage.removeItem('customThemeSlug');
    } catch (error) {
      console.warn('localStorage.clear failed:', error);
    }
  },
};

export const RestaurantProvider = ({ children }) => {
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restaurantId, setRestaurantId] = useState(null);
  const [tableNumber, setTableNumber] = useState(null);
  // Seed themeId from cache so the correct theme bundle can load on first paint
  // (before the restaurant row finishes fetching), avoiding a theme flash.
  const [themeId, setThemeId] = useState(
    () => localStorageStore.get('themeId') || DEFAULT_THEME_ID,
  );
  const [themeType, setThemeType] = useState(
    () => localStorageStore.get('themeType') || DEFAULT_THEME_TYPE,
  );
  const [customThemeSlug, setCustomThemeSlug] = useState(
    () => localStorageStore.get('customThemeSlug') || null,
  );
  const [previewThemeParam] = useState(() => getPreviewThemeFromUrl());
  const isThemePreview = Boolean(previewThemeParam);
  const { loading: authLoading } = useAuth();

  const resolveCustomSlug = async (rid, tid, ttype) => {
    if (!isCustomThemeActive(tid, ttype)) return null;
    return getCustomThemeSlug(rid, tid);
  };

  // Keep themeId in sync with restaurant row, or ?previewTheme= override (session-only).
  useEffect(() => {
    if (!restaurant?.id) return;

    let cancelled = false;

    const applyTheme = async () => {
      if (previewThemeParam) {
        const preview = await resolveThemePreview(restaurant.id, previewThemeParam);
        if (cancelled) return;
        if (preview) {
          setThemeId(preview.themeId);
          setThemeType(preview.themeType);
          setCustomThemeSlug(preview.customThemeSlug);
          return;
        }
      }

      const resolvedId = restaurant.theme_id || DEFAULT_THEME_ID;
      const resolvedType = restaurant.theme_type || DEFAULT_THEME_TYPE;
      setThemeId(resolvedId);
      setThemeType(resolvedType);
      localStorageStore.set('themeId', resolvedId);
      localStorageStore.set('themeType', resolvedType);

      if (!isCustomThemeActive(resolvedId, resolvedType)) {
        setCustomThemeSlug(null);
        localStorageStore.remove('customThemeSlug');
        return;
      }

      const slug = await resolveCustomSlug(restaurant.id, resolvedId, resolvedType);
      if (cancelled) return;
      setCustomThemeSlug(slug);
      if (slug) localStorageStore.set('customThemeSlug', slug);
    };

    applyTheme();

    return () => {
      cancelled = true;
    };
  }, [restaurant?.id, restaurant?.theme_id, restaurant?.theme_type, previewThemeParam]);

  useEffect(() => {
    // Check URL parameters first
    const urlParams = getUrlParams();

    // Check if we have URL parameters
    if (urlParams.restaurantId && urlParams.tableNumber) {
      // Store in localStorage
      localStorageStore.set('restaurantId', urlParams.restaurantId);
      localStorageStore.set('tableNumber', urlParams.tableNumber);

      setRestaurantId(urlParams.restaurantId);
      setTableNumber(urlParams.tableNumber);
    } else {
      const storedRestaurantId = localStorageStore.get('restaurantId');
      const storedTableNumber = localStorageStore.get('tableNumber');

      if (storedRestaurantId && storedTableNumber) {
        setRestaurantId(storedRestaurantId);
        setTableNumber(storedTableNumber);
      } else {
        // No stored restaurant, don't set any default
        setLoading(false);
        return;
      }
    }
  }, []);

  useEffect(() => {
    // Still waiting for auth to resolve — don't touch loading, just wait.
    // The initial loading=true state is correct during this time.
    if (authLoading) return;

    // Auth done, but no restaurant ID found — nothing to fetch
    if (!restaurantId) {
      setLoading(false);
      return;
    }

    const abortController = new AbortController();

    const fetchRestaurant = async () => {
      try {
        setLoading(true);
        setError(null);

        // Instant load from cache if available
        const cachedRestaurant = await offlineStorage.getRestaurant(restaurantId);
        if (cachedRestaurant && !abortController.signal.aborted) {
          setRestaurant(cachedRestaurant);
          // Keep loading=true so background fetch can finish
        }

        // Fetch restaurant
        const { data: restaurantData, error: restaurantError } = await supabase
          .from('restaurants')
          .select('*')
          .eq('id', restaurantId)
          .single();

        if (abortController.signal.aborted) return;

        if (restaurantError) {
          if (
            restaurantError.code === 'PGRST301' ||
            restaurantError.message?.includes('JWT') ||
            restaurantError.message?.includes('expired') ||
            restaurantError.message?.includes('Invalid Refresh Token') ||
            restaurantError.message?.includes('Refresh Token Not Found') ||
            restaurantError.status === 401
          ) {
            setError('AUTH_ERROR');
            return;
          }
          setError(restaurantError.message);
          return;
        }

        if (!restaurantData) {
          localStorageStore.clear();
          setError('Restaurant not found');
          return;
        }

        // Still set restaurant data so UI can show the name even if inactive/suspended
        setRestaurant({
          ...restaurantData,
          subscription_status: null,
          subscription_end_date: null,
        });

        if (!restaurantData.is_active) {
          localStorageStore.clear();
          setError('Restaurant is currently inactive');
          return;
        }

        // Check if restaurant has an active subscription (includes 7-day grace window after expiry)
        const { data: subscription, error: subscriptionError } = await supabase
          .from('subscriptions')
          .select('id, status, end_date')
          .eq('restaurant_id', restaurantId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (abortController.signal.aborted) return;

        if (subscriptionError) {
          if (
            subscriptionError.code === 'PGRST301' ||
            subscriptionError.message?.includes('JWT') ||
            subscriptionError.message?.includes('expired') ||
            subscriptionError.message?.includes('Invalid Refresh Token') ||
            subscriptionError.message?.includes('Refresh Token Not Found') ||
            subscriptionError.status === 401
          ) {
            setError('AUTH_ERROR');
            return;
          }
        }

        // Determine if restaurant is accessible:
        // - active / grace_period: always accessible
        // - expired: allow 7-day grace window after end_date before blocking
        // - no subscription row: block (matches getActiveRestaurants selection page behaviour)
        // - cancelled: block
        const isAccessible = (() => {
          if (!subscription) return false;
          if (subscription.status === 'active' || subscription.status === 'grace_period')
            return true;
          if (subscription.status === 'expired' && subscription.end_date) {
            const daysSinceExpiry = Math.ceil(
              (Date.now() - new Date(subscription.end_date).getTime()) / (1000 * 60 * 60 * 24),
            );
            return daysSinceExpiry <= 7;
          }
          // 'cancelled' or unknown status — block
          return false;
        })();

        if (!isAccessible) {
          localStorageStore.clear();
          setError('This restaurant is currently unavailable. Please try again later.');
          return;
        }

        setRestaurant({
          ...restaurantData,
          subscription_status: subscription?.status ?? null,
          subscription_end_date: subscription?.end_date ?? null,
        });

        offlineStorage.saveRestaurant({
          ...restaurantData,
          subscription_status: subscription?.status ?? null,
          subscription_end_date: subscription?.end_date ?? null,
        });
      } catch (err) {
        // AbortError is expected on unmount / re-render — suppress it silently
        if (abortController.signal.aborted || err?.name === 'AbortError') return;

        if (
          err?.code === 'PGRST301' ||
          err?.message?.includes('JWT') ||
          err?.message?.includes('Invalid Refresh Token') ||
          err?.message?.includes('Refresh Token Not Found') ||
          err?.status === 401
        ) {
          setError('AUTH_ERROR');
        } else {
          setError(err?.message || 'Failed to load restaurant data');
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    // Hard safety timeout — if fetch takes > 8s, unblock the UI
    const safetyTimer = setTimeout(() => setLoading(false), 8000);

    fetchRestaurant().finally(() => clearTimeout(safetyTimer));

    return () => abortController.abort();
  }, [restaurantId, authLoading]);

  // Re-fetch theme when user returns to the customer tab (e.g. after changing theme in dashboard).
  useEffect(() => {
    if (!restaurantId || authLoading) return;

    const syncThemeFromServer = async () => {
      if (getPreviewThemeFromUrl()) return;

      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select('theme_id, theme_type')
          .eq('id', restaurantId)
          .maybeSingle();

        if (error || !data) return;

        const slug = await getCustomThemeSlug(restaurantId, data.theme_id);

        setRestaurant((prev) => {
          if (!prev || prev.id !== restaurantId) return prev;
          if (prev.theme_id === data.theme_id && prev.theme_type === data.theme_type) {
            return prev;
          }
          const next = {
            ...prev,
            theme_id: data.theme_id,
            theme_type: data.theme_type,
          };
          offlineStorage.saveRestaurant(next);
          return next;
        });

        if (slug) {
          setCustomThemeSlug(slug);
          localStorageStore.set('customThemeSlug', slug);
        }
      } catch {
        // ignore background sync errors
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        syncThemeFromServer();
      }
    };

    const onFocus = () => syncThemeFromServer();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [restaurantId, authLoading]);

  const updateRestaurant = async (updates) => {
    if (!restaurantId) return false;

    try {
      const { data, error } = await supabase
        .from('restaurants')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', restaurantId)
        .select()
        .single();

      if (error) {
        return false;
      }

      setRestaurant(data);
      return true;
    } catch (err) {
      return false;
    }
  };

  const refreshRestaurant = async () => {
    if (!restaurantId) return false;

    try {
      setLoading(true);

      // Fetch restaurant
      const { data: restaurantData, error: restaurantError } = await supabase
        .from('restaurants')
        .select('*')
        .eq('id', restaurantId)
        .single();

      if (restaurantError || !restaurantData || !restaurantData.is_active) {
        // Check if it's an authentication error
        if (
          restaurantError?.code === 'PGRST301' ||
          restaurantError?.message?.includes('JWT') ||
          restaurantError?.message?.includes('expired') ||
          restaurantError?.message?.includes('Invalid Refresh Token') ||
          restaurantError?.message?.includes('Refresh Token Not Found') ||
          restaurantError?.status === 401
        ) {
          setError('AUTH_ERROR');
        } else {
          setError(restaurantError?.message || 'Restaurant not found or inactive');
        }
        return false;
      }

      // Check subscription status — use same grace window logic as fetchRestaurant
      const { data: subscription, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('id, status, end_date')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subscriptionError) {
        if (
          subscriptionError.code === 'PGRST301' ||
          subscriptionError.message?.includes('JWT') ||
          subscriptionError.message?.includes('Invalid Refresh Token') ||
          subscriptionError.message?.includes('Refresh Token Not Found') ||
          subscriptionError.status === 401
        ) {
          setError('AUTH_ERROR');
          return false;
        }
      }

      // Apply same grace window: allow expired restaurants up to 7 days past end_date.
      // null subscription = trust is_active on restaurant; banner handles owner messaging.
      const isAccessible = (() => {
        if (!subscription) return true;
        if (subscription.status === 'active' || subscription.status === 'grace_period') return true;
        if (subscription.status === 'expired' && subscription.end_date) {
          const daysSinceExpiry = Math.ceil(
            (Date.now() - new Date(subscription.end_date).getTime()) / (1000 * 60 * 60 * 24),
          );
          return daysSinceExpiry <= 7;
        }
        return false;
      })();

      if (!isAccessible) {
        setError('This restaurant is currently unavailable. Please try again later.');
        return false;
      }

      setRestaurant({
        ...restaurantData,
        subscription_status: subscription?.status ?? null,
        subscription_end_date: subscription?.end_date ?? null,
      });
      setError(null);
      return true;
    } catch (err) {
      if (err?.name === 'AbortError') return false;
      if (
        err?.code === 'PGRST301' ||
        err?.message?.includes('JWT') ||
        err?.message?.includes('Invalid Refresh Token') ||
        err?.message?.includes('Refresh Token Not Found') ||
        err?.status === 401
      ) {
        setError('AUTH_ERROR');
      } else {
        setError('Failed to refresh restaurant data');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const setRestaurantAndTable = (newRestaurantId, newTableNumber) => {
    // Clear old restaurant data
    setRestaurant(null);
    setError(null);

    // Update localStorage
    localStorageStore.set('restaurantId', newRestaurantId);
    localStorageStore.set('tableNumber', newTableNumber);

    // Update state
    setRestaurantId(newRestaurantId);
    setTableNumber(newTableNumber);

    // Clear any cached food data for the old restaurant
    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('fooddata_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear cached food data from sessionStorage:', error);
    }
  };

  const clearSession = () => {
    localStorageStore.clear();
    setRestaurantId(null);
    setTableNumber(null);
    setRestaurant(null);
    setThemeId(DEFAULT_THEME_ID);
    setThemeType(DEFAULT_THEME_TYPE);
    setCustomThemeSlug(null);
    setError(null);
  };

  const value = {
    restaurant,
    restaurantId,
    tableNumber,
    themeId,
    themeType,
    customThemeSlug,
    isThemePreview,
    previewThemeParam,
    updateRestaurant,
    refreshRestaurant,
    setRestaurantAndTable,
    clearSession,
    loading,
    error,
  };

  return <RestaurantContext.Provider value={value}>{children}</RestaurantContext.Provider>;
};

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
};
